/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Client-side computation of structurally-derivable molecular properties.
 * Used to fill in or correct properties from AI-generated molecules where
 * the model may omit or misreport values that can be derived deterministically
 * from the atom + bond table.
 *
 * Reliable from any V2000 mol block:
 *   - atomCount, bondCount, heavyAtomCount, formula, molecularMass — pure counts
 *   - hydrogenBondDonorCount (Lipinski) — N/O atoms with at least one H neighbor
 *   - hydrogenBondAcceptorCount (Lipinski) — count of N + O atoms
 *   - rotatableBondCount — single, non-ring, between heavy atoms each w/ ≥2
 *     heavy neighbors, excluding amide bonds
 *
 * Requires explicit hydrogens (run AddHydrogens first if the SDF carries
 * heavy atoms only):
 *   - molecularMass (off by H mass per missing H)
 *   - hydrogenBondDonorCount (relies on visible N-H / O-H bonds)
 *
 * NOT computed: density, boilingPoint, meltingPoint — these need experimental
 * tables or group-contribution methods that are unreliable enough to mislead.
 */

import { MoleculeAtom, MoleculeBond, parseLooseSdf } from './sdfNormalize.ts';
import { beadComposition } from './models/beadModel.ts';
import type { BeadModel } from './types.ts';

// IUPAC standard atomic weights (2021), abridged to elements that occur in
// drug-like / organic molecules. Anything missing contributes 0 mass — better
// than fabricating a value.
const ATOMIC_WEIGHTS: Record<string, number> = {
  H: 1.008,
  He: 4.003,
  Li: 6.94,
  Be: 9.012,
  B: 10.81,
  C: 12.011,
  N: 14.007,
  O: 15.999,
  F: 18.998,
  Ne: 20.18,
  Na: 22.99,
  Mg: 24.305,
  Al: 26.982,
  Si: 28.085,
  P: 30.974,
  S: 32.06,
  Cl: 35.45,
  Ar: 39.948,
  K: 39.098,
  Ca: 40.078,
  Sc: 44.956,
  Ti: 47.867,
  V: 50.942,
  Cr: 51.996,
  Mn: 54.938,
  Fe: 55.845,
  Co: 58.933,
  Ni: 58.693,
  Cu: 63.546,
  Zn: 65.38,
  Ga: 69.723,
  Ge: 72.63,
  As: 74.922,
  Se: 78.971,
  Br: 79.904,
  Kr: 83.798,
  Rb: 85.468,
  Sr: 87.62,
  I: 126.904,
};

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';
const subscript = (n: number): string =>
  String(n)
    .split('')
    .map((d) => SUBSCRIPT_DIGITS[+d])
    .join('');

interface Adjacency {
  // byAtom[i] is the list of (neighbor index, bond index) pairs for atom i.
  // Tracking bond index lets per-bond predicates (skip-this-bond BFS for ring
  // membership) avoid double-walking parallel edges.
  byAtom: Array<Array<{ atom: number; bondIdx: number }>>;
}

const buildAdjacency = (n: number, bonds: MoleculeBond[]): Adjacency => {
  const byAtom: Adjacency['byAtom'] = Array.from({ length: n }, () => []);
  bonds.forEach((b, idx) => {
    if (b.a >= 0 && b.a < n && b.b >= 0 && b.b < n && b.a !== b.b) {
      byAtom[b.a].push({ atom: b.b, bondIdx: idx });
      byAtom[b.b].push({ atom: b.a, bondIdx: idx });
    }
  });
  return { byAtom };
};

// A bond is in a ring iff its endpoints remain connected with that bond cut.
// O(B * (V + E)) — fine for the ≤500-atom regime we cap simulation at.
const computeBondRingMembership = (n: number, bonds: MoleculeBond[], adj: Adjacency): boolean[] => {
  return bonds.map((bond, bondIdx) => {
    if (bond.a < 0 || bond.b < 0 || bond.a >= n || bond.b >= n) return false;
    const visited = new Array<boolean>(n).fill(false);
    const queue: number[] = [bond.a];
    visited[bond.a] = true;
    while (queue.length) {
      const u = queue.shift()!;
      if (u === bond.b) return true;
      for (const { atom: v, bondIdx: bi } of adj.byAtom[u]) {
        if (bi === bondIdx) continue;
        if (visited[v]) continue;
        visited[v] = true;
        queue.push(v);
      }
    }
    return false;
  });
};

const heavyDegree = (atomIdx: number, atoms: MoleculeAtom[], adj: Adjacency): number => {
  let count = 0;
  for (const { atom: v } of adj.byAtom[atomIdx]) {
    if (atoms[v].element !== 'H') count++;
  }
  return count;
};

const hasDoubleBondTo = (
  atomIdx: number,
  targetEl: string,
  atoms: MoleculeAtom[],
  bonds: MoleculeBond[],
  adj: Adjacency,
): boolean => {
  for (const { atom: v, bondIdx } of adj.byAtom[atomIdx]) {
    if (bonds[bondIdx].order === 2 && atoms[v].element === targetEl) return true;
  }
  return false;
};

export interface ComputedStructuralProperties {
  atomCount: number;
  bondCount: number;
  heavyAtomCount: number;
  molecularMass: number;
  formula: string;
  hydrogenBondDonorCount: number;
  hydrogenBondAcceptorCount: number;
  rotatableBondCount: number;
  // Fsp3 = (sp3 carbons) / (total carbons). Lovering 2009 found higher
  // Fsp3 correlates with downstream clinical success — a useful sanity
  // check against flat aromatic structures the AI can over-produce.
  fsp3: number;
  // Total ring count (cyclomatic number on the heavy-atom subgraph:
  // E - V + components). Counts independent cycles, so naphthalene = 2,
  // biphenyl (single bond between two rings) = 2, benzene = 1.
  ringCount: number;
  // Subset of ringCount restricted to fully aromatic rings (every
  // member bond has order 4). Required input for QED's AROM term.
  aromaticRingCount: number;
}

// Fsp3 over the heavy-atom subgraph. An sp3 carbon has no double / triple /
// aromatic bonds; we read that directly off the AtomBondContext we already
// build for TPSA. Returns 0 when there are no carbons (matches RDKit's
// convention — undefined would just push NaN through downstream UI rows).
const computeFsp3FromContexts = (atoms: MoleculeAtom[], contexts: AtomBondContext[]): number => {
  let total = 0;
  let sp3 = 0;
  for (let i = 0; i < atoms.length; i++) {
    if (atoms[i].element !== 'C') continue;
    total++;
    const c = contexts[i];
    if (c.numDouble === 0 && c.numTriple === 0 && c.numAromatic === 0) sp3++;
  }
  return total > 0 ? sp3 / total : 0;
};

// Total ring count via cyclomatic number on the heavy-atom subgraph:
// rings = E - V + C, where E = heavy bonds, V = heavy atoms, C = connected
// components of that subgraph. Matches RDKit's NumRings descriptor.
const computeRingCountHeavy = (atoms: MoleculeAtom[], bonds: MoleculeBond[], adj: Adjacency): number => {
  const n = atoms.length;
  const heavy: boolean[] = atoms.map((a) => a.element !== 'H');
  let v = 0;
  for (const h of heavy) if (h) v++;
  let e = 0;
  for (const b of bonds) {
    if (b.a < 0 || b.b < 0 || b.a >= n || b.b >= n) continue;
    if (heavy[b.a] && heavy[b.b]) e++;
  }
  // Components on heavy-only subgraph (BFS).
  const visited = new Array<boolean>(n).fill(false);
  let components = 0;
  for (let i = 0; i < n; i++) {
    if (!heavy[i] || visited[i]) continue;
    components++;
    const stack: number[] = [i];
    visited[i] = true;
    while (stack.length) {
      const u = stack.pop()!;
      for (const { atom: w } of adj.byAtom[u]) {
        if (!heavy[w] || visited[w]) continue;
        visited[w] = true;
        stack.push(w);
      }
    }
  }
  return Math.max(0, e - v + components);
};

// Elements that can sit in an aromatic π system (sp2-capable in organic
// chemistry). Anything outside this set makes its ring non-aromatic.
const AROMATIC_ELEMENTS = new Set(['C', 'N', 'O', 'S', 'P', 'Se', 'B']);

// Smallest ring (as a Set of atom indices) passing through one bond — BFS
// shortest path between its endpoints with that bond removed. null if the
// bond isn't in a ring.
const smallestRingThroughBond = (
  bondIdx: number,
  bonds: MoleculeBond[],
  adj: Adjacency,
  n: number,
): Set<number> | null => {
  const bond = bonds[bondIdx];
  if (bond.a < 0 || bond.b < 0 || bond.a >= n || bond.b >= n) return null;
  const start = bond.a;
  const goal = bond.b;
  const prev = new Array<number>(n).fill(-2); // -2 = unvisited, -1 = root sentinel
  const queue: number[] = [start];
  prev[start] = -1;
  while (queue.length) {
    const u = queue.shift()!;
    if (u === goal) break;
    for (const { atom: v, bondIdx: bi } of adj.byAtom[u]) {
      if (bi === bondIdx) continue; // the removed bond can't close the cycle
      if (prev[v] !== -2) continue;
      prev[v] = u;
      queue.push(v);
    }
  }
  if (prev[goal] === -2) return null;
  const ring = new Set<number>();
  let cur = goal;
  while (cur !== -1) {
    ring.add(cur);
    cur = prev[cur];
  }
  return ring;
};

// Smallest-set-of-smallest-rings (SSSR-ish): the unique smallest cycle through
// each bond. Good enough for drug-like molecules — yields the two hexagons of
// naphthalene, not the 10-membered perimeter. Each ring is its atom-index set.
const computeSSSR = (n: number, bonds: MoleculeBond[], adj: Adjacency): Set<number>[] => {
  const rings: Set<number>[] = [];
  const seen = new Set<string>();
  for (let bi = 0; bi < bonds.length; bi++) {
    const ring = smallestRingThroughBond(bi, bonds, adj, n);
    if (!ring) continue;
    const key = [...ring].sort((x, y) => x - y).join(',');
    if (seen.has(key)) continue;
    seen.add(key);
    rings.push(ring);
  }
  return rings;
};

// Is one ring aromatic? Handles BOTH encodings:
//   • Aromatic-flagged SDF (every ring bond order 4): trust the flag.
//   • Kekulé SDF (alternating single/double, orders 1/2): apply Hückel's rule —
//     every ring atom must be sp2 (conjugated) and the π-electron count ≡ 2
//     (mod 4). This is the case AI generation and many SDF sources emit; the
//     old order-4-only count silently returned 0 for them (benzene → 0).
const isAromaticRing = (ring: Set<number>, atoms: MoleculeAtom[], bonds: MoleculeBond[], adj: Adjacency): boolean => {
  // A chordless SSSR ring's edges are exactly each atom's bonds to its two ring
  // neighbors. If every such bond is order 4, the structure is already
  // aromatic-perceived — trust it (this also covers heteroaromatics like
  // pyrrole that a π count from flags alone can't get right).
  let allRingBondsOrder4 = true;
  let ringBondsSeen = 0;
  for (const i of ring) {
    for (const { atom: v, bondIdx } of adj.byAtom[i]) {
      if (!ring.has(v)) continue;
      ringBondsSeen++;
      if (bonds[bondIdx].order !== 4) allRingBondsOrder4 = false;
    }
  }
  if (ringBondsSeen > 0 && allRingBondsOrder4) return true;

  // Kekulé / mixed: Hückel π count.
  let piElectrons = 0;
  for (const i of ring) {
    const el = atoms[i].element;
    if (!AROMATIC_ELEMENTS.has(el)) return false;
    let doubleInRing = false;
    let aromaticFlag = false;
    let tripleAny = false;
    let doubleExoToHetero = false; // e.g. a ring C of a carbonyl (pyranone)
    for (const { atom: v, bondIdx } of adj.byAtom[i]) {
      if (atoms[v].element === 'H') continue;
      const order = bonds[bondIdx].order;
      if (order === 3) tripleAny = true;
      else if (order === 4) aromaticFlag = true;
      else if (order === 2) {
        if (ring.has(v)) doubleInRing = true;
        else if (atoms[v].element === 'O' || atoms[v].element === 'N' || atoms[v].element === 'S')
          doubleExoToHetero = true;
      }
    }
    if (tripleAny) return false; // sp atom — never aromatic
    if (aromaticFlag || doubleInRing) {
      piElectrons += 1; // sp2 atom with an in-ring (aromatic/Kekulé) double bond
    } else if (doubleExoToHetero) {
      return false; // strict-Kekulé carbonyl breaks aromaticity
    } else if (el !== 'C') {
      piElectrons += 2; // heteroatom lone pair in the π system (pyrrole/furan/thiophene)
    } else {
      return false; // sp3 carbon — ring not aromatic
    }
  }
  return piElectrons >= 2 && piElectrons % 4 === 2;
};

// Count aromatic rings over the SSSR (see isAromaticRing). Benzene → 1,
// naphthalene → 2, biphenyl → 2, pyridine/pyrrole/furan/thiophene → 1.
const computeAromaticRingCount = (atoms: MoleculeAtom[], bonds: MoleculeBond[], adj: Adjacency): number => {
  let count = 0;
  for (const ring of computeSSSR(atoms.length, bonds, adj)) {
    if (isAromaticRing(ring, atoms, bonds, adj)) count++;
  }
  return count;
};

export const computeStructuralProperties = (
  atoms: MoleculeAtom[],
  bonds: MoleculeBond[],
): ComputedStructuralProperties => {
  const n = atoms.length;
  const adj = buildAdjacency(n, bonds);
  const inRing = computeBondRingMembership(n, bonds, adj);

  let mass = 0;
  let heavy = 0;
  const counts = new Map<string, number>();
  for (const a of atoms) {
    const w = ATOMIC_WEIGHTS[a.element];
    if (w) mass += w;
    if (a.element !== 'H') heavy++;
    counts.set(a.element, (counts.get(a.element) ?? 0) + 1);
  }

  // Hill system: C first, H second, then everything else alphabetically.
  const others = [...counts.keys()].filter((s) => s !== 'C' && s !== 'H').sort();
  const ordered = [...(counts.has('C') ? ['C'] : []), ...(counts.has('H') ? ['H'] : []), ...others];
  let formula = '';
  for (const s of ordered) {
    formula += s;
    const c = counts.get(s)!;
    if (c > 1) formula += subscript(c);
  }

  let donors = 0;
  for (let i = 0; i < n; i++) {
    const el = atoms[i].element;
    if (el !== 'N' && el !== 'O') continue;
    for (const { atom: v } of adj.byAtom[i]) {
      if (atoms[v].element === 'H') {
        donors++;
        break;
      }
    }
  }

  let acceptors = 0;
  for (const a of atoms) {
    if (a.element === 'N' || a.element === 'O') acceptors++;
  }

  // Rotatable bonds: single, non-ring, both endpoints heavy with ≥2 heavy
  // neighbors, not an amide (C-N where the C also has a =O).
  let rotatable = 0;
  for (let bi = 0; bi < bonds.length; bi++) {
    const b = bonds[bi];
    if (b.order !== 1) continue;
    if (inRing[bi]) continue;
    const ea = atoms[b.a]?.element;
    const eb = atoms[b.b]?.element;
    if (!ea || !eb || ea === 'H' || eb === 'H') continue;
    if (heavyDegree(b.a, atoms, adj) < 2 || heavyDegree(b.b, atoms, adj) < 2) continue;
    const isAmide =
      (ea === 'C' && eb === 'N' && hasDoubleBondTo(b.a, 'O', atoms, bonds, adj)) ||
      (ea === 'N' && eb === 'C' && hasDoubleBondTo(b.b, 'O', atoms, bonds, adj));
    if (isAmide) continue;
    rotatable++;
  }

  const contexts = buildAtomBondContexts(atoms, bonds, adj);
  const fsp3 = computeFsp3FromContexts(atoms, contexts);
  const ringCount = computeRingCountHeavy(atoms, bonds, adj);
  const aromaticRingCount = computeAromaticRingCount(atoms, bonds, adj);

  return {
    atomCount: n,
    bondCount: bonds.length,
    heavyAtomCount: heavy,
    molecularMass: mass,
    formula,
    hydrogenBondDonorCount: donors,
    hydrogenBondAcceptorCount: acceptors,
    rotatableBondCount: rotatable,
    fsp3,
    ringCount,
    aromaticRingCount,
  };
};

/**
 * Convenience wrapper: parse an SDF and compute structural properties in one
 * call. Returns null if no atoms can be parsed from the input.
 */
export const computeStructuralPropertiesForSdf = (sdf: string): ComputedStructuralProperties | null => {
  const mol = parseLooseSdf(sdf);
  if (!mol || mol.atoms.length === 0) return null;
  return computeStructuralProperties(mol.atoms, mol.bonds);
};

// ─── Topological Polar Surface Area (Ertl 2000) ──────────────────────────────
//
// Reference: Ertl, P.; Rohde, B.; Selzer, P. "Fast calculation of molecular
//   polar surface area as a sum of fragment-based contributions and its
//   application to the prediction of drug transport properties."
//   J. Med. Chem. 2000, 43, 3714-3717.
//
// Each polar atom (N, O, S, P) contributes a fixed surface-area amount based
// on its bonding pattern: number of single/double/triple/aromatic bonds to
// non-H, number of H neighbors, and aromatic ring membership. The table below
// covers the common patterns from Ertl's Table 1; uncovered patterns fall
// back to 0 (and are summed without warning — Ertl's defaults for rare cases
// are themselves ~0).

interface AtomBondContext {
  isAromatic: boolean; // any incident bond has order 4 (aromatic)
  numH: number;
  numSingle: number; // single bonds to non-H
  numDouble: number;
  numTriple: number;
  numAromatic: number; // aromatic (order-4) bonds to non-H
}

const buildAtomBondContexts = (atoms: MoleculeAtom[], bonds: MoleculeBond[], adj: Adjacency): AtomBondContext[] => {
  return atoms.map((_, i) => {
    let numH = 0;
    let numSingle = 0;
    let numDouble = 0;
    let numTriple = 0;
    let numAromatic = 0;
    for (const { atom: v, bondIdx } of adj.byAtom[i]) {
      const order = bonds[bondIdx].order;
      if (atoms[v].element === 'H') {
        numH++;
        continue;
      }
      if (order === 4) numAromatic++;
      else if (order === 3) numTriple++;
      else if (order === 2) numDouble++;
      else numSingle++;
    }
    return {
      isAromatic: numAromatic > 0,
      numH,
      numSingle,
      numDouble,
      numTriple,
      numAromatic,
    };
  });
};

// Ertl Table 1 fragment contributions (Å²). Patterns ordered most-specific
// first; the first matching row is used. Aromatic patterns require ≥2
// aromatic bonds; aliphatic patterns require 0.
const ertlContribution = (el: string, ctx: AtomBondContext): number => {
  const { isAromatic, numH, numSingle, numDouble, numTriple, numAromatic } = ctx;

  if (el === 'N') {
    if (isAromatic) {
      if (numH === 0 && numAromatic === 2 && numSingle === 0) return 12.89; // [n](:*):*
      if (numH === 0 && numAromatic === 3) return 4.41; // [n](:*)(:*):* (3 aromatic neighbors)
      if (numH === 0 && numAromatic === 2 && numSingle === 1) return 4.93; // [n](-*)(:*):*
      if (numH === 1 && numAromatic === 2) return 15.79; // [nH](:*):*
      return 12.89; // fallback aromatic N
    }
    if (numTriple === 1) return 23.79; // [N]#*
    if (numH === 0 && numDouble === 2) return 11.68; // [N](=*)(=*) (azide-style)
    if (numH === 0 && numDouble === 1 && numSingle === 1) return 12.36; // [N](-*)=*
    if (numH === 0 && numSingle === 3) return 3.24; // [N](-*)(-*)(-*)
    if (numH === 1 && numDouble === 1) return 23.85; // [NH]=*
    if (numH === 1 && numSingle === 2) return 12.03; // [NH](-*)(-*)
    if (numH === 2 && numSingle === 1) return 26.02; // [NH2](-*)
    if (numH === 2 && numDouble === 1) return 23.85; // [NH2]=* (rare)
    if (numH === 3) return 27.64; // [NH3] (free ammonia / NH4+ cap)
    return 3.24; // unparameterized N
  }

  if (el === 'O') {
    if (isAromatic) return 13.14; // [o](:*):*
    if (numH === 0 && numDouble === 1) return 17.07; // [O]=*
    if (numH === 0 && numSingle === 2) return 9.23; // [O](-*)(-*)
    if (numH === 1 && numSingle === 1) return 20.23; // [OH](-*)
    if (numH === 0 && numSingle === 1) return 23.06; // [O-](-*) (assume anion)
    return 9.23;
  }

  if (el === 'S') {
    if (isAromatic) return 28.24; // [s]
    if (numH === 0 && numDouble === 2) return 8.38; // sulfonyl =S(=O)
    if (numH === 0 && numDouble === 1) return 32.09; // [S]=*
    if (numH === 0 && numSingle === 2) return 25.3; // [S](-*)(-*)
    if (numH === 1 && numSingle === 1) return 38.8; // [SH](-*)
    return 25.3;
  }

  if (el === 'P') {
    if (numH === 0 && numDouble === 1) return 9.81; // [P]=O
    if (numH === 0 && numSingle === 3) return 13.59; // [P](-*)(-*)(-*)
    if (numH === 1 && numSingle === 2) return 23.47; // [PH](-*)(-*)
    return 13.59;
  }

  return 0;
};

export const computePolarSurfaceArea = (atoms: MoleculeAtom[], bonds: MoleculeBond[]): number => {
  const adj = buildAdjacency(atoms.length, bonds);
  const ctxs = buildAtomBondContexts(atoms, bonds, adj);
  let psa = 0;
  for (let i = 0; i < atoms.length; i++) {
    psa += ertlContribution(atoms[i].element, ctxs[i]);
  }
  return psa;
};

// ─── Crippen / Wildman logP (atomic contributions) ───────────────────────────
//
// Reference: Wildman, S. A.; Crippen, G. M. "Prediction of physicochemical
//   parameters by atomic contributions." J. Chem. Inf. Comput. Sci. 1999,
//   39, 868-873.
//
// Each atom is assigned to one of 68 SMARTS-defined types, each with a fixed
// contribution. We don't have a SMARTS engine, so we implement a conservative
// rule-based classifier that covers the common types for organic molecules.
// Accuracy is typically within ±0.5 logP units of the canonical Wildman-Crippen
// value for drug-like molecules; less for unusual heterocycles or charged
// species. The result is labeled "computed" so callers can prefer an AI-
// reported value when available.

interface CrippenContext extends AtomBondContext {
  // True if the atom is bonded to any halogen.
  hasHalogenNeighbor: boolean;
  // True if the atom is bonded to any oxygen.
  hasOxygenNeighbor: boolean;
  // True if the atom is bonded to any nitrogen.
  hasNitrogenNeighbor: boolean;
}

const HALOGENS = new Set(['F', 'Cl', 'Br', 'I']);

const buildCrippenContexts = (atoms: MoleculeAtom[], bonds: MoleculeBond[], adj: Adjacency): CrippenContext[] => {
  const base = buildAtomBondContexts(atoms, bonds, adj);
  return atoms.map((_, i) => {
    let hasHalogenNeighbor = false;
    let hasOxygenNeighbor = false;
    let hasNitrogenNeighbor = false;
    for (const { atom: v } of adj.byAtom[i]) {
      const el = atoms[v].element;
      if (HALOGENS.has(el)) hasHalogenNeighbor = true;
      else if (el === 'O') hasOxygenNeighbor = true;
      else if (el === 'N') hasNitrogenNeighbor = true;
    }
    return { ...base[i], hasHalogenNeighbor, hasOxygenNeighbor, hasNitrogenNeighbor };
  });
};

const crippenContribution = (el: string, ctx: CrippenContext): number => {
  const {
    isAromatic,
    numH,
    numSingle,
    numDouble,
    numTriple,
    hasHalogenNeighbor,
    hasOxygenNeighbor,
    hasNitrogenNeighbor,
  } = ctx;
  const heteroNeighbor = hasHalogenNeighbor || hasOxygenNeighbor || hasNitrogenNeighbor;

  if (el === 'C') {
    if (isAromatic) {
      // Aromatic carbons: C18-C24 in Wildman-Crippen, ~0.3-0.5
      if (heteroNeighbor) return 0.245; // C19/C20: aromatic C bonded to heteroatom
      return 0.294; // C18: plain aromatic C
    }
    if (numTriple > 0) return 0.029; // C9 sp carbon
    if (numDouble > 0) {
      if (heteroNeighbor) return -0.0023; // C5: =C bonded to hetero
      return 0.5437; // C3/C4: olefinic
    }
    // sp3 C
    if (heteroNeighbor) {
      // C2-class: aliphatic C bonded to halogen/N/O.
      if (numH === 0) return 0.029;
      if (numH === 1) return -0.2035;
      if (numH === 2) return -0.2051;
      return -0.2783; // CH3 next to hetero (e.g. methoxy methyl)
    }
    if (numH === 0) return 0.1581; // C1 quaternary
    if (numH === 1) return 0.003; // C1 methine
    if (numH === 2) return -0.0516; // C1 methylene
    return -0.1188; // C1 methyl
  }

  // H is classified by its neighbor's element in computeCrippenLogP — we never
  // dispatch into this function with el === 'H'.

  if (el === 'N') {
    if (isAromatic) {
      if (numH === 1) return -0.3251; // N11 [nH]
      return -0.7096; // N12 aromatic N no H
    }
    if (numTriple > 0) return -0.3239; // N9 nitrile
    if (numDouble > 0) return -0.4458; // N7 imine
    if (numH === 0) return 0.1836; // N1 tertiary amine
    if (numH === 1) return -0.019; // N2 secondary
    return -0.3187; // N3 primary amine
  }

  if (el === 'O') {
    if (isAromatic) return 0.1129; // O7 furan-like
    if (numDouble > 0) return -0.0399; // O5/O6 carbonyl/sulfonyl (rough)
    if (numH > 0) return -0.2893; // O2 hydroxyl
    return -0.0684; // O3 ether
  }

  if (el === 'F') return 0.4202; // F1
  if (el === 'Cl') return 0.6895; // Cl1
  if (el === 'Br') return 0.8456; // Br1
  if (el === 'I') return 0.8857; // I1
  if (el === 'S') {
    if (numH > 0) return 0.6482; // thiol
    return 0.6482; // thioether
  }
  if (el === 'P') return 0.8612;

  return 0; // unparameterized — silently skipped
};

const polarHValue = -0.2486; // H attached to N/O/S

export const computeCrippenLogP = (atoms: MoleculeAtom[], bonds: MoleculeBond[]): number => {
  const adj = buildAdjacency(atoms.length, bonds);
  const ctxs = buildCrippenContexts(atoms, bonds, adj);
  let logp = 0;
  for (let i = 0; i < atoms.length; i++) {
    if (atoms[i].element === 'H') {
      // Decide H type from its single neighbor (if any).
      const neighbors = adj.byAtom[i];
      let polar = false;
      for (const { atom: v } of neighbors) {
        const el = atoms[v].element;
        if (el === 'N' || el === 'O' || el === 'S') {
          polar = true;
          break;
        }
      }
      logp += polar ? polarHValue : 0.123;
      continue;
    }
    logp += crippenContribution(atoms[i].element, ctxs[i]);
  }
  return logp;
};

/**
 * Convenience wrapper: parse an SDF and compute TPSA + Crippen logP.
 * Returns null if no atoms can be parsed.
 */
export const computeDescriptorPropertiesForSdf = (sdf: string): { polarSurfaceArea: number; logP: number } | null => {
  const mol = parseLooseSdf(sdf);
  if (!mol || mol.atoms.length === 0) return null;
  return {
    polarSurfaceArea: computePolarSurfaceArea(mol.atoms, mol.bonds),
    logP: computeCrippenLogP(mol.atoms, mol.bonds),
  };
};

// ─── Drug-likeness rule flags ────────────────────────────────────────────────
//
// Lipinski's "Rule of Five" (Lipinski et al. 1997, 2001): poor oral
// absorption / permeation when ≥2 of the four cutoffs are violated. The
// pass flag here is the standard binary "all four cutoffs satisfied".
//
// Veber's rule (Veber et al. 2002): both rotatable-bond and PSA cutoffs
// must hold for good oral bioavailability.

export interface DrugLikenessInputs {
  molecularMass: number;
  logP: number;
  hydrogenBondDonorCount: number;
  hydrogenBondAcceptorCount: number;
  rotatableBondCount: number;
  polarSurfaceArea: number;
}

export const lipinskiPasses = (i: DrugLikenessInputs): boolean =>
  i.molecularMass <= 500 && i.logP <= 5 && i.hydrogenBondDonorCount <= 5 && i.hydrogenBondAcceptorCount <= 10;

export const veberPasses = (i: DrugLikenessInputs): boolean => i.rotatableBondCount <= 10 && i.polarSurfaceArea <= 140;

/**
 * Rehydrate computed properties for an existing project's molecules. For each
 * molecule with SDF data, recompute the structurally-derivable fields + TPSA
 * + Crippen logP + Lipinski/Veber flags and merge them into the project's
 * `generatedMolecularProperties` map under the molecule's name.
 *
 * Why this exists: projects saved before client-side property computation
 * existed have stale `generatedMolecularProperties` entries (often missing
 * flags, sometimes carrying AI-fabricated values). Wiring this into the
 * project loader (cloud fetch + persist middleware merge) lets old projects
 * inherit current computation without a manual edit.
 *
 * AI-only fields (smiles, inChI, description, density, boilingPoint,
 * meltingPoint, complexity, logS) are preserved from the existing entry.
 *
 * Mutates `generatedProps` in place.
 */
export const rehydrateComputedPropertiesFromSdf = (
  molecules: Array<{ name: string; data?: string }> | undefined | null,
  generatedProps: Record<string, any> | undefined | null,
): void => {
  if (!molecules || !generatedProps) return;
  for (const m of molecules) {
    if (!m.data) continue;
    // A coarse-grained molecule: its SDF holds placeholder elements, so the
    // formula line stays the bead composition (beadModel.ts).
    const beads = (m as { beads?: BeadModel }).beads;
    const structural = computeStructuralPropertiesForSdf(m.data);
    const descriptors = computeDescriptorPropertiesForSdf(m.data);
    if (!structural || !descriptors) continue;
    const inputs: DrugLikenessInputs = {
      molecularMass: structural.molecularMass,
      logP: descriptors.logP,
      hydrogenBondDonorCount: structural.hydrogenBondDonorCount,
      hydrogenBondAcceptorCount: structural.hydrogenBondAcceptorCount,
      rotatableBondCount: structural.rotatableBondCount,
      polarSurfaceArea: descriptors.polarSurfaceArea,
    };
    const qed = computeQED({
      molecularMass: structural.molecularMass,
      logP: descriptors.logP,
      hydrogenBondDonorCount: structural.hydrogenBondDonorCount,
      hydrogenBondAcceptorCount: structural.hydrogenBondAcceptorCount,
      polarSurfaceArea: descriptors.polarSurfaceArea,
      rotatableBondCount: structural.rotatableBondCount,
      aromaticRingCount: structural.aromaticRingCount,
    });
    const existing = generatedProps[m.name];
    if (existing) {
      // AI-generated molecule (or one whose generatedProps was already
      // populated by a prior import). Overwrite the structural / descriptor
      // / drug-likeness fields — old projects predate these computations
      // and may carry AI-fabricated values or missing pass flags. AI-only
      // identifiers (smiles, inChI, description) and thermo (density, bp,
      // mp, complexity, logS) are preserved.
      existing.atomCount = structural.atomCount;
      existing.bondCount = structural.bondCount;
      existing.heavyAtomCount = structural.heavyAtomCount;
      existing.molecularMass = structural.molecularMass;
      existing.formula = beads ? beadComposition(beads) : structural.formula;
      existing.hydrogenBondDonorCount = structural.hydrogenBondDonorCount;
      existing.hydrogenBondAcceptorCount = structural.hydrogenBondAcceptorCount;
      existing.rotatableBondCount = structural.rotatableBondCount;
      existing.polarSurfaceArea = descriptors.polarSurfaceArea;
      existing.logP = descriptors.logP;
      existing.lipinskiPasses = lipinskiPasses(inputs);
      existing.veberPasses = veberPasses(inputs);
      existing.fsp3 = structural.fsp3;
      existing.ringCount = structural.ringCount;
      existing.aromaticRingCount = structural.aromaticRingCount;
      existing.qed = qed;
    } else {
      // Internal-DB molecule (no generatedProps entry yet). The CSV row in
      // providedMolecularProperties carries the curated experimental fields
      // (density / bp / mp / etc.) and identifiers; we just need to create
      // a minimal generatedProps entry that supplies the structurally-
      // derived fields the CSV doesn't have. mergeMolecularProperties will
      // overlay CSV finite values on top of these per-field, so we don't
      // shadow curated data.
      generatedProps[m.name] = {
        atomCount: structural.atomCount,
        bondCount: structural.bondCount,
        heavyAtomCount: structural.heavyAtomCount,
        molecularMass: structural.molecularMass,
        formula: beads ? beadComposition(beads) : structural.formula,
        hydrogenBondDonorCount: structural.hydrogenBondDonorCount,
        hydrogenBondAcceptorCount: structural.hydrogenBondAcceptorCount,
        rotatableBondCount: structural.rotatableBondCount,
        polarSurfaceArea: descriptors.polarSurfaceArea,
        logP: descriptors.logP,
        lipinskiPasses: lipinskiPasses(inputs),
        veberPasses: veberPasses(inputs),
        fsp3: structural.fsp3,
        ringCount: structural.ringCount,
        aromaticRingCount: structural.aromaticRingCount,
        qed,
      };
    }
  }
};

// ─── QED — Quantitative Estimate of Drug-likeness (Bickerton 2012) ───────────
//
// Reference: Bickerton, G. R.; Paolini, G. V.; Besnard, J.; Muresan, S.;
//   Hopkins, A. L. "Quantifying the chemical beauty of drugs." Nat. Chem.
//   2012, 4, 90-98 (and Supporting Information Table S2).
//
// Each of the eight descriptors (MW, ALogP, HBA, HBD, PSA, rotatable bonds,
// aromatic rings, structural alerts) is mapped through an asymmetric double
// sigmoid (ADS) into a desirability d ∈ (0, 1). QED_w is the weighted
// geometric mean of the eight desirabilities. We omit the "structural alerts"
// term — implementing it would need a SMARTS engine for the 116 unwanted-
// substructure patterns. We feed alerts=0 (no PAINS-like flags), so the
// reported QED is the optimistic upper bound for any molecule that *would*
// have triggered alerts. This is a conservative approximation, not a re-
// implementation of the full method — sufficient for ranking and rule-of-
// thumb filtering, not for publication.

interface AdsParams {
  A: number;
  B: number;
  C: number;
  D: number;
  E: number;
  F: number;
  DMAX: number;
}

// Bickerton 2012 Table S2 — verbatim parameters for the eight desirability
// functions. Keys mirror the paper's naming.
const ADS_PARAMS: Record<string, AdsParams> = {
  MW: { A: 2.817, B: 392.575, C: 290.749, D: 2.42, E: 49.222, F: 65.371, DMAX: 104.981 },
  ALOGP: { A: 3.172, B: 137.862, C: 2.534, D: 4.581, E: 0.823, F: 0.576, DMAX: 131.319 },
  HBA: { A: 2.948, B: 160.461, C: 3.615, D: 4.435, E: 0.29, F: 1.3, DMAX: 148.778 },
  HBD: { A: 1.619, B: 1010.051, C: 0.985, D: 0.0, E: 0.713, F: 0.92, DMAX: 258.163 },
  PSA: { A: 1.877, B: 125.223, C: 62.901, D: 87.348, E: 12.02, F: 28.504, DMAX: 104.98 },
  ROTB: { A: 0.011, B: 272.405, C: 2.558, D: 1.566, E: 1.272, F: 2.749, DMAX: 105.31 },
  AROM: { A: 3.217, B: 957.741, C: 2.53, D: 0.0, E: 0.851, F: 0.33, DMAX: 312.331 },
  ALERTS: { A: 0.011, B: 93.103, C: -2.0, D: 0.0, E: 0.0, F: 1.151, DMAX: 6.989 },
};

// Bickerton 2012 QED_w weights (unitless, expressing relative importance).
const QED_WEIGHTS = {
  MW: 0.66,
  ALOGP: 0.46,
  HBA: 0.05,
  HBD: 0.61,
  PSA: 0.06,
  ROTB: 0.65,
  AROM: 0.48,
  ALERTS: 0.95,
};

// Asymmetric double sigmoid. The product of two sigmoids gives a peaked
// curve; the (A, B, C, D, E, F) tuple shifts and scales it so the peak sits
// over the histogram mode of drug-like molecules for each descriptor. We
// divide by DMAX so the output is normalized to ≤ 1 at the peak.
const ads = (x: number, p: AdsParams): number => {
  const left = 1 / (1 + Math.exp((-1 * (x - p.C + p.D / 2)) / p.E));
  // The right-side sigmoid is undefined when F = 0 (HBD, AROM in Bickerton's
  // table). In those cases the second factor collapses to 1 — the desirability
  // is a one-sided sigmoid plateauing at high x.
  const right = p.F === 0 ? 1 : 1 - 1 / (1 + Math.exp((-1 * (x - p.C - p.D / 2)) / p.F));
  return (p.A + p.B * left * right) / p.DMAX;
};

export interface QEDInputs {
  molecularMass: number;
  logP: number;
  hydrogenBondDonorCount: number;
  hydrogenBondAcceptorCount: number;
  polarSurfaceArea: number;
  rotatableBondCount: number;
  aromaticRingCount: number;
  // Optional override: number of structural-alert hits. We don't compute it
  // (no SMARTS engine), so callers can pass 0 (the default — optimistic
  // upper bound) or supply their own count when an alert checker exists.
  structuralAlerts?: number;
}

export const computeQED = (i: QEDInputs): number => {
  const d = {
    MW: ads(i.molecularMass, ADS_PARAMS.MW),
    ALOGP: ads(i.logP, ADS_PARAMS.ALOGP),
    HBA: ads(i.hydrogenBondAcceptorCount, ADS_PARAMS.HBA),
    HBD: ads(i.hydrogenBondDonorCount, ADS_PARAMS.HBD),
    PSA: ads(i.polarSurfaceArea, ADS_PARAMS.PSA),
    ROTB: ads(i.rotatableBondCount, ADS_PARAMS.ROTB),
    AROM: ads(i.aromaticRingCount, ADS_PARAMS.AROM),
    ALERTS: ads(i.structuralAlerts ?? 0, ADS_PARAMS.ALERTS),
  };
  // Weighted geometric mean: exp( Σ w_i ln d_i / Σ w_i ). Clamp d_i above a
  // tiny epsilon to avoid -∞ when an outlier descriptor sits in the
  // sigmoid's near-zero tail.
  const EPS = 1e-9;
  let num = 0;
  let den = 0;
  for (const key of Object.keys(QED_WEIGHTS) as (keyof typeof QED_WEIGHTS)[]) {
    const w = QED_WEIGHTS[key];
    num += w * Math.log(Math.max(EPS, d[key]));
    den += w;
  }
  const qed = Math.exp(num / den);
  // Clamp into [0, 1] — sigmoid normalization can drift slightly outside
  // due to the DMAX divisor in Bickerton's parameters.
  return Math.max(0, Math.min(1, qed));
};
